import { Form, Formik, type FormikHelpers } from "formik";
import { toast } from "sonner";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SelectField } from "@/features/settings/components/select-field";
import {
  createTransactionCode,
  type TransactionCategory,
  TRANSACTION_CATEGORY_OPTIONS,
  type TransactionCode,
  updateTransactionCode,
} from "@/features/reference/api";
import { applyApiErrors } from "@/lib/form-errors";

const schema = Yup.object({
  code: Yup.string()
    .trim()
    .matches(/^\d{1,5}$/, "Code is 1–5 digits")
    .required("Code is required"),
  description: Yup.string().trim().max(200, "Max 200 characters").required("Description is required"),
  category: Yup.string().required(),
});

interface FormValues {
  code: string;
  description: string;
  category: TransactionCategory;
}

interface Props {
  code?: TransactionCode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

export function TransactionCodeDialog({ code, open, onOpenChange, onSaved }: Props) {
  const isEdit = !!code;

  async function handleSubmit(values: FormValues, { setErrors }: FormikHelpers<FormValues>) {
    try {
      if (isEdit) await updateTransactionCode(code.id, values);
      else await createTransactionCode(values);
      onSaved();
      onOpenChange(false);
      toast.success(isEdit ? "Code updated" : "Code added");
    } catch (err) {
      toast.error(applyApiErrors(err, { setErrors }, { fallback: "Couldn't save the code." }));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit transaction code" : "Add transaction code"}</DialogTitle>
          <DialogDescription>IRS account-transcript code (Document 11734 vocabulary).</DialogDescription>
        </DialogHeader>
        <Formik<FormValues>
          initialValues={{
            code: code?.code ?? "",
            description: code?.description ?? "",
            category: code?.category ?? "MISC",
          }}
          validationSchema={schema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="grid gap-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <FormField name="code" label="Code" placeholder="150" />
                <SelectField name="category" label="Category" options={TRANSACTION_CATEGORY_OPTIONS} />
              </div>
              <FormField name="description" label="Description" />
              <DialogFooter>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Saving..." : isEdit ? "Save changes" : "Add code"}
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
